import CryptoJS from "crypto-js";
import forge from "node-forge";
import { BASE_URL } from "./constants";

// Request/response encryption, the same scheme the REM screens in
// phloan-frontend use (services/rem/MasterService.js):
// - request: the JSON body is AES-256-CBC encrypted with a random key + IV,
//   and that key is RSA-OAEP encrypted with the public key. The gateway
//   decrypts it.
// - response: the "is_encrypt_response" header makes rem-backend return
//   { encrypted_data, encrypted_symmetric_key, iv }. The symmetric key is
//   AES-256-CBC encrypted with SHA-256(response AES key) and a zero IV.
const PUBLIC_KEY = (process.env.REACT_APP_PUBLIC_KEY || "").replace(/\\n/g, "\n");
const RESPONSE_AES_KEY = process.env.REACT_APP_RESPONSE_AES_KEY || "";

// A local gateway doesn't decrypt request bodies, so (as in phloan-frontend)
// nothing is encrypted against localhost, nor when the keys aren't set.
// REACT_APP_ENCRYPTION_ENABLED=false turns it off for a build (stage).
export const ENCRYPTION_ENABLED = process.env.REACT_APP_ENCRYPTION_ENABLED !== "false" && !!PUBLIC_KEY && !!RESPONSE_AES_KEY && !/localhost|127\.0\.0\.1/.test(BASE_URL);

export const ENCRYPT_RESPONSE_HEADER = "is_encrypt_response";

let rsaKey = null;
const publicKey = () => rsaKey || (rsaKey = forge.pki.publicKeyFromPem(PUBLIC_KEY));

export const encryptPayload = (payload) => {
  const symmetricKey = CryptoJS.lib.WordArray.random(32);
  const iv = CryptoJS.lib.WordArray.random(16);
  const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(payload), symmetricKey, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
  const keyBytes = forge.util.hexToBytes(symmetricKey.toString(CryptoJS.enc.Hex));
  return {
    encrypted_data: encryptedData,
    encrypted_symmetric_key: forge.util.encode64(publicKey().encrypt(keyBytes, "RSA-OAEP")),
    iv: iv.toString(CryptoJS.enc.Base64),
  };
};

export const decryptPayload = ({ encrypted_data, encrypted_symmetric_key, iv }) => {
  const keyBase64 = CryptoJS.AES.decrypt(encrypted_symmetric_key, CryptoJS.SHA256(RESPONSE_AES_KEY), {
    iv: CryptoJS.lib.WordArray.create(new Array(4).fill(0)),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString(CryptoJS.enc.Utf8);
  const json = CryptoJS.AES.decrypt(encrypted_data, CryptoJS.enc.Base64.parse(keyBase64), {
    iv: CryptoJS.enc.Base64.parse(iv),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString(CryptoJS.enc.Utf8);
  return JSON.parse(json);
};

// The encrypted block sits at the top level for handlers that build their
// own envelope, and under "data" for everything else. Either way it is
// swapped back in place, so callers see the same shape as unencrypted.
export const decryptResponseBody = (body) => {
  if (body?.encrypted_symmetric_key) return decryptPayload(body);
  if (body?.data?.encrypted_symmetric_key) return { ...body, data: decryptPayload(body.data) };
  return body;
};
